import React, { SyntheticEvent, useEffect, useState } from 'react';

import { ErrorMessages } from 'common/components/Form/ErrorMessages';
import withFormState, {
  WithFormStateProps
} from 'common/components/Form/withFormState';
import Checkbox from 'common/components/SignUpForm/Checkbox';
import styles from 'common/components/TOSValidationModal/styles.module.scss';
import { IMG_CGU_URL, ROUTE } from 'common/configuration/constants';
import { isTrue } from 'common/forms/validators';
import ReactModal from 'common/module/ui/lazy-modal/react-modal';
import { path } from 'common/tools/network/routing';
import trans from 'common/tools/translations/trans';
import { sendGA4Event } from 'common/tracking/sendGA4Event';

const PARTENAIRES_PDF_URL =
  'https://assets.allocine.fr/acsta/v9/allocine/legal/partenaires_targett_allocine.pdf';

export type TOSValidationModalParams = {
  showAdConsentCheckbox?: boolean;
  showPixelConsentCheckbox?: boolean;
};

export type TOSValidationProps = TOSValidationModalParams & {
  errorMessages: string[];
  onSubmit: (
    termsOfService?: boolean,
    adConsent?: boolean,
    pixelConsent?: boolean
  ) => void;
  submitting: boolean;
};
export type TOSValidationField = 'termsOfService';

const title = trans('userspace.tos-valisation-modal.title');
const description = trans('userspace.tos-valisation-modal.description');

const TOSValidation = ({
  errorMessages,
  formErrors,
  formValues,
  isFormValidating,
  onFieldChange,
  onSubmit,
  showAdConsentCheckbox = true,
  showPixelConsentCheckbox = false,
  submitting,
  validateForm
}: WithFormStateProps<TOSValidationField> & TOSValidationProps) => {
  const [checked, setChecked] = useState(!!formValues.termsOfService);
  const [adConsentChecked, setAdConsentChecked] = useState(false);
  const [pixelConsentChecked, setPixelConsentChecked] = useState(false);

  useEffect(
    () => onFieldChange('termsOfService', checked),
    [checked, onFieldChange]
  );

  useEffect(() => {
    let type = 'cgu';
    if (showAdConsentCheckbox && showPixelConsentCheckbox)
      type = 'cgu|consent|pixel';
    else if (showAdConsentCheckbox) type = 'cgu|consent';
    const payload = { type, site_route: ROUTE };
    sendGA4Event('modal_impression', payload);
  }, [showAdConsentCheckbox, showPixelConsentCheckbox]);

  const handleSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();
    const isValid = await validateForm();
    if (isValid) {
      if (showAdConsentCheckbox) {
        const consent = adConsentChecked ? 'consent_yes' : 'consent_no';
        const pixel = pixelConsentChecked ? 'pixel_yes' : 'pixel_no';
        const payload = {
          type: showPixelConsentCheckbox ? 'cgu|consent|pixel' : 'cgu|consent',
          value: showPixelConsentCheckbox ? `${consent}|${pixel}` : consent,
          site_route: ROUTE
        };
        sendGA4Event('button_click', payload);
      }
      onSubmit(
        formValues.termsOfService,
        showAdConsentCheckbox ? adConsentChecked : undefined,
        showPixelConsentCheckbox ? pixelConsentChecked : undefined
      );
    }
  };

  const loading = isFormValidating || submitting;
  const isCguChecked = !!formValues.termsOfService;
  const isSubmitDisabled = !isCguChecked;

  return (
    <ReactModal
      className={styles.modal}
      preventClose
      size="medium"
      title={title}
    >
      <form onSubmit={handleSubmit} noValidate>
        <img className={styles.image} src={IMG_CGU_URL} alt="" />
        {!showAdConsentCheckbox && (
          <div className={styles.description}>{description}</div>
        )}
        <div className={styles.checkboxLabels}>
          <Checkbox
            errors={formErrors.termsOfService}
            label={trans('userspace.sign-up.conditions-of-use', {
              conditionsOfUse: path('cgu')
            })}
            labelClassName={styles.checkboxLabelText}
            name="termsOfService"
            onChange={() => setChecked(!checked)}
            value={checked}
          />
          {showAdConsentCheckbox && (
            <Checkbox
              label={trans('userspace.tos-validation-modal.ad-consent-label', {
                partenairesLink: PARTENAIRES_PDF_URL
              })}
              labelClassName={styles.checkboxLabelText}
              name="adConsent"
              onChange={() => setAdConsentChecked(!adConsentChecked)}
              value={adConsentChecked}
            />
          )}
          {showPixelConsentCheckbox && (
            <Checkbox
              label={trans(
                'userspace.tos-validation-modal.pixel-consent-label'
              )}
              labelClassName={styles.checkboxLabelText}
              name="pixelConsent"
              onChange={() => setPixelConsentChecked(!pixelConsentChecked)}
              value={pixelConsentChecked}
            />
          )}
        </div>
        {showAdConsentCheckbox && (
          <p
            className={styles.legalText}
            dangerouslySetInnerHTML={{
              __html: trans('userspace.tos-validation-modal.legal-text', {
                monAllocineLink: path('userspace'),
                privacyPolicyLink: path('privacy_policy')
              })
            }}
          />
        )}
        <ErrorMessages messages={errorMessages} />
        {loading ? (
          <div className="button button-loading">...</div>
        ) : (
          <button
            type="submit"
            className={`form-input button button-md button-inverse-full submit-button ${styles.submitButton} ${isSubmitDisabled ? styles.buttonDisabled : ''}`}
            disabled={isSubmitDisabled}
          >
            {trans('userspace.validate')}
          </button>
        )}
      </form>
    </ReactModal>
  );
};

export default withFormState<TOSValidationProps, TOSValidationField>({
  fields: ['termsOfService'],
  defaultValues: { termsOfService: false },
  validators: {
    termsOfService: [isTrue(trans('error.conditions-of-use'))]
  }
})(TOSValidation);
